import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Label } from '@cargomatic/ui';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import moment from 'moment-timezone';
import DatePicker from 'react-datepicker';
import EditButton from '../../../v2/buttons/EditButton';
import Badge from '../../../v2/Badge';
import DateFormatter from 'utils/formatters/Date';
import { getClassName } from 'utils/ui/classes';
import { useFormState } from '../hooks/useFormState';
const ShipmentDetailDateItem = ({ dataTestId, title, titleClassName, className, value: propValue, editable, onSave, param, useNamedUpdate, badge, locale, heapClassName, timezone, }) => {
    const { t } = useTranslation();
    const { showEdit, formValue, setFormValue, startEditing, cancelEditing, saveChanges, } = useFormState(propValue);
    useEffect(() => {
        if (!showEdit) {
            setFormValue(propValue);
        }
    }, [propValue, showEdit, setFormValue]);
    const handleSave = () => {
        // Normalize selected calendar date to midnight in the provided timezone (if any)
        let valueToSave = formValue;
        if (timezone && formValue) {
            const m = moment(formValue);
            if (m.isValid()) {
                const ymd = m.format('YYYY-MM-DD');
                valueToSave = moment
                    .tz(`${ymd} 00:00:00`, 'YYYY-MM-DD HH:mm:ss', timezone)
                    .toISOString();
            }
        }
        if (param && useNamedUpdate) {
            saveChanges(() => onSave(param, valueToSave));
        }
        else {
            saveChanges(() => onSave(valueToSave));
        }
    };
    const renderInlineEditing = () => {
        if (!showEdit) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "div-fc1a27c8", className: "" },
            React.createElement(DatePicker, { key: 0, selected: DateFormatter.getMomentDate(formValue), className: "form-field", locale: locale, onChange: (newValue) => setFormValue(newValue), placeholderText: t('SET_A_DATE'), isClearable: true }),
            React.createElement("div", { "data-testid": "div-8dac46bc", className: "flex items-center mt-2 space-x-2" },
                React.createElement(Button, { "data-testid": "button-ed2eaf43", key: 2, onClick: handleSave, className: `carrier-shipmentDetailDateItem-editAppointment heap ${heapClassName}` }, titleize(t('SAVE'))),
                React.createElement(Button, { "data-testid": "button-0ad88495", key: 1, variant: "secondary", onClick: () => cancelEditing(propValue) }, t('CANCEL')))));
    };
    const renderBadge = () => {
        if (!badge) {
            return null;
        }
        let color = '';
        switch (badge) {
            case 'OPENTRACK':
                color = 'grey';
                break;
            case 'NOT_REPORTED':
                color = 'grey';
                break;
            default:
                color = 'green';
        }
        return (React.createElement(Badge, { dataTestId: "Badge-278d9b60", color: color, size: "small" }, t(badge)));
    };
    const renderDate = () => {
        if (showEdit) {
            return null;
        }
        const displayDataTestId = `${dataTestId}-display`;
        const editButtonDataTestId = `${dataTestId}-edit-button`;
        return (React.createElement("div", { "data-testid": "div-977c0dbb", className: "flex items-center space-x-2" },
            React.createElement("span", { "data-testid": displayDataTestId }, DateFormatter.formatDate(formValue, {
                t,
                dateFormat: 'L',
                timezone,
            })),
            editable && (React.createElement(EditButton, { dataTestId: editButtonDataTestId, title: t('EDIT_DATE'), disabled: !editable, onClick: () => startEditing(propValue) }))));
    };
    const labelDataTestId = `${dataTestId}-label`;
    return (React.createElement("div", { "data-testid": dataTestId, className: getClassName('mb-4', className) },
        React.createElement("div", { "data-testid": labelDataTestId, className: "flex flex-row" },
            React.createElement(Label, { "data-testid": labelDataTestId, className: getClassName('mb-1 space-x-1 form-label', titleClassName) }, title),
            renderBadge()),
        renderDate(),
        renderInlineEditing()));
};
ShipmentDetailDateItem.propTypes = {
    dataTestId: PropTypes.string,
    title: PropTypes.string.isRequired,
    titleClassName: PropTypes.string,
    className: PropTypes.string,
    value: PropTypes.string,
    editable: PropTypes.bool,
    onSave: PropTypes.func,
    param: PropTypes.string,
    useNamedUpdate: PropTypes.bool,
    badge: PropTypes.string,
    locale: PropTypes.string,
    heapClassName: PropTypes.string,
    timezone: PropTypes.string,
};
ShipmentDetailDateItem.defaultProps = {
    dataTestId: 'ShipmentDetailDateItem',
    titleClassName: '',
    className: '',
    value: '',
    editable: false,
    param: '',
    useNamedUpdate: false,
    badge: '',
    locale: 'en',
    heapClassName: '',
};
export default ShipmentDetailDateItem;
